import React, { useEffect, useRef, useCallback } from 'react';
import { createSwapy } from 'swapy';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { GripVertical, Ticket, TrendingUp, ChevronLeft, ChevronRight, Settings2, Maximize2, Minimize2, Square, RectangleHorizontal, RectangleVertical, Eye, EyeOff, MoreHorizontal, Monitor } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useDashboardLayout } from '@/hooks/useDashboardLayout';
import DashboardLayoutConfig from './DashboardLayoutConfig';
import DashboardConfigManager from './DashboardConfigManager';
import ErrorBoundary from '@/components/ErrorBoundary';

// Componentes dos cards
import DebugMetrics from './DebugMetrics';
import ResponseTimeMetrics from './ResponseTimeMetrics';
import PriorityDistribution from './PriorityDistribution';
import TrendChart from './TrendChart';
import SatisfactionMetrics from './SatisfactionMetrics';
import CustomerSatisfaction from './CustomerSatisfaction';
import ResolutionRate from './ResolutionRate';
import TopCategories from './TopCategories';
import TopAnalysts from './TopAnalysts';

interface SwapyDashboardProps {
  responseTimeMetrics: any;
  priorityDistribution: any;
  trendData: any;
  satisfactionMetrics: any;
  topCategories: any;
  topAnalysts: any;
  metricsLoading: boolean;
  // Props para cards fixos
  tickets?: any[];
  paginatedTickets?: any[];
  currentPage?: number;
  totalPages?: number;
  goToPrevPage?: () => void;
  goToNextPage?: () => void;
  handleTicketClick?: (ticket: any) => void;
  getPriorityColor?: (priority: string) => string;
  getStatusColor?: (status: string) => string;
  getSLAStatus?: (ticket: any) => any;
  slaMetrics?: any[];
  format?: any;
  ptBR?: any;
}

const SwapyDashboard: React.FC<SwapyDashboardProps> = ({
  responseTimeMetrics,
  priorityDistribution,
  trendData,
  satisfactionMetrics,
  topCategories,
  topAnalysts,
  metricsLoading,
  // Props para cards fixos
  tickets = [],
  paginatedTickets = [],
  currentPage = 1,
  totalPages = 1,
  goToPrevPage = () => {},
  goToNextPage = () => {},
  handleTicketClick = () => {},
  getPriorityColor = () => '',
  getStatusColor = () => '',
  getSLAStatus = () => ({}),
  slaMetrics = [],
  format,
  ptBR,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const swapyRef = useRef<any>(null);

  const {
    cards = [],
    allCards = [],
    toggleCardVisibility,
    resetLayout,
  } = useDashboardLayout() || {};

  // Função para alterar o tamanho de um card
  const resizeCard = useCallback((cardId: string, newSize: 'small' | 'medium' | 'large' | 'wide' | 'tall' | 'extra-wide') => {
    try {
      if (!allCards || allCards.length === 0) {
        console.error('allCards não está disponível');
        return;
      }

      const updatedCards = allCards.map(card => {
        if (card.id === cardId) {
          let colSpan = 1;
          let rowSpan = 1;
          
          switch (newSize) {
            case 'small':
              colSpan = 1;
              rowSpan = 1;
              break;
            case 'medium':
              colSpan = 1;
              rowSpan = 1;
              break;
            case 'large':
              colSpan = 1;
              rowSpan = 2;
              break;
            case 'wide':
              colSpan = 2;
              rowSpan = 1;
              break;
            case 'tall':
              colSpan = 1;
              rowSpan = 3;
              break;
            case 'extra-wide':
              colSpan = 4;
              rowSpan = 1;
              break;
          }
          
          return { ...card, size: newSize, colSpan, rowSpan };
        }
        return card;
      });
      
      // Salvar no localStorage
      localStorage.setItem('dashboard-layout', JSON.stringify(updatedCards));
      
      // Mostrar feedback ao usuário
      toast.success('Card redimensionado!', {
        description: 'Recarregue a página para ver as mudanças.',
        action: {
          label: 'Recarregar',
          onClick: () => window.location.reload()
        }
      });
      
    } catch (error) {
      console.error('Erro ao redimensionar card:', error);
    }
  }, [allCards]);

  // Função para obter classes CSS baseadas no tamanho do card
  const getCardClasses = (card: any) => {
    const baseClasses = "min-h-[200px]";
    const colSpan = card.colSpan || 1;
    const rowSpan = card.rowSpan || 1;
    
    let sizeClasses = "";
    
    // Classes de coluna (col-span)
    switch (colSpan) {
      case 1:
        sizeClasses += " col-span-1";
        break;
      case 2:
        sizeClasses += " col-span-1 md:col-span-2";
        break;
      case 3:
        sizeClasses += " col-span-1 md:col-span-2 lg:col-span-3";
        break;
      case 4:
        sizeClasses += " col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4";
        break;
      default:
        sizeClasses += " col-span-1";
    }
    
    // Classes de linha (row-span) - usando classes específicas para garantir que existam no Tailwind
    switch (rowSpan) {
      case 2:
        sizeClasses += " row-span-2";
        break;
      case 3:
        sizeClasses += " row-span-3";
        break;
      case 4:
        sizeClasses += " row-span-4";
        break;
      default:
        // row-span-1 é padrão, não precisa adicionar
        break;
    }
    
    // Classes de altura baseadas no tamanho
    switch (card.size) {
      case 'small':
        sizeClasses += " min-h-[150px]";
        break;
      case 'medium':
        sizeClasses += " min-h-[200px]";
        break;
      case 'large':
        sizeClasses += " min-h-[300px]";
        break;
      case 'wide':
        sizeClasses += " min-h-[200px]";
        break;
      case 'tall':
        sizeClasses += " min-h-[400px]";
        break;
      case 'extra-wide':
        sizeClasses += " min-h-[200px]";
        break;
      default:
        sizeClasses += " min-h-[200px]";
    }
    
    return `${baseClasses}${sizeClasses}`;
  };

  // Função para renderizar componente baseado no ID
  const renderCardComponent = (cardId: string) => {
    switch (cardId) {
      case 'debug':
        return <DebugMetrics />;
      case 'response-times':
        return (
          <ResponseTimeMetrics 
            data={responseTimeMetrics} 
            isLoading={metricsLoading} 
          />
        );
      case 'priority-distribution':
        return (
          <PriorityDistribution 
            data={priorityDistribution} 
            isLoading={metricsLoading} 
          />
        );
      case 'trend-chart':
        return (
          <TrendChart 
            data={trendData} 
            isLoading={metricsLoading} 
          />
        );
      case 'satisfaction':
        return (
          <SatisfactionMetrics 
            data={satisfactionMetrics} 
            isLoading={metricsLoading} 
          />
        );
      case 'customer-satisfaction':
        return (
          <CustomerSatisfaction 
            data={satisfactionMetrics} 
            isLoading={metricsLoading} 
          />
        );
      case 'resolution-rate':
        return (
          <ResolutionRate 
            data={satisfactionMetrics} 
            isLoading={metricsLoading} 
          />
        );
      case 'top-categories':
        return (
          <TopCategories 
            categories={topCategories} 
            isLoading={metricsLoading} 
          />
        );
      case 'top-analysts':
        return (
          <TopAnalysts 
            analysts={topAnalysts} 
            isLoading={metricsLoading} 
          />
        );
      case 'recent-tickets':
        return (
          <React.Fragment key="recent-tickets-content">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-600 text-lg md:text-xl font-bold">
                <Ticket className="h-6 w-6" />
                Tickets Recentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {tickets.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    Nenhum ticket encontrado
                  </div>
                ) : (
                  <React.Fragment key="paginated-tickets">
                    {paginatedTickets.map((ticket: any) => {
                      const slaStatus = getSLAStatus(ticket);
                      return (
                        <div 
                          key={ticket.id} 
                          className="flex items-center justify-between p-4 rounded-lg bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-200 transition-all duration-200 cursor-pointer group"
                          onClick={() => handleTicketClick(ticket)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <span className="font-semibold text-blue-600 text-lg">{ticket.numero}</span>
                              <Badge className={`${getPriorityColor(ticket.prioridade)} capitalize shadow`}>
                                {ticket.prioridade}
                              </Badge>
                            </div>
                            <p className="text-base font-medium text-gray-700 mb-1 truncate">{ticket.titulo}</p>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-gray-500">Cliente:</span>
                              <span className="text-xs font-semibold text-gray-700">
                                {ticket.cliente?.name || 'Não informado'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={`${getStatusColor(ticket.status)} shadow-sm`}>
                                {ticket.status.replace('_', ' ')}
                              </Badge>
                              <Badge className={`${slaStatus.color} shadow-sm`}>
                                {slaStatus.label}
                              </Badge>
                              <span className="text-xs text-gray-400">
                                {format && ptBR ? format(new Date(ticket.criado_em), 'dd/MM/yyyy', { locale: ptBR }) : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="text-sm text-gray-500">
                          Página {currentPage} de {totalPages} ({tickets.length} tickets)
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={goToPrevPage}
                            disabled={currentPage === 1}
                            className="flex items-center gap-1"
                          >
                            <ChevronLeft className="h-4 w-4" />
                            Anterior
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={goToNextPage}
                            disabled={currentPage === totalPages}
                            className="flex items-center gap-1"
                          >
                            Próximo
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                )}
              </div>
            </CardContent>
          </React.Fragment>
        );
      case 'sla-performance':
        return (
          <React.Fragment key="sla-performance-content">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-green-600 text-lg md:text-xl font-bold">
                  <TrendingUp className="h-6 w-6" />
                  Performance SLA
                </div>
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                  Tempo Real
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {slaMetrics && slaMetrics.length > 0 ? (
                  <React.Fragment key="sla-metrics-content">
                    {/* SLA Geral Summary */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-gray-800">SLA Geral</h3>
                        <div className="flex items-center gap-2">
                          <span className={`text-2xl font-bold ${
                            slaMetrics.reduce((acc: number, m: any) => acc + m.current * m.total, 0) / slaMetrics.reduce((acc: number, m: any) => acc + m.total, 0) >= 95 
                              ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {Math.round(slaMetrics.reduce((acc: number, m: any) => acc + m.current * m.total, 0) / slaMetrics.reduce((acc: number, m: any) => acc + m.total, 0) || 0)}%
                          </span>
                          <span className="text-sm text-gray-500">
                            ({slaMetrics.reduce((acc: number, m: any) => acc + m.total, 0)} total)
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-600">Meta: 95%</span>
                        <span className="text-green-600 font-medium">
                          ✓ {slaMetrics.reduce((acc: number, m: any) => acc + (m.onTime || 0), 0)} no prazo
                        </span>
                        <span className="text-red-600 font-medium">
                          ⚠ {slaMetrics.reduce((acc: number, m: any) => acc + (m.breached || 0), 0)} violados
                        </span>
                      </div>
                    </div>

                    {/* Individual Metrics */}
                    {slaMetrics.map((metric: any, index: number) => (
                      <div key={metric.category || `sla-metric-${index}`} className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-700">{metric.category || `Métrica ${index + 1}`}</h4>
                          <div className="flex items-center gap-2">
                            <span className={`text-lg font-bold ${metric.current >= 95 ? 'text-green-600' : 'text-red-600'}`}>
                              {metric.current}%
                            </span>
                            <span className="text-sm text-gray-500">({metric.total} tickets)</span>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${metric.current >= 95 ? 'bg-green-500' : 'bg-red-500'}`}
                            style={{ width: `${metric.current}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </React.Fragment>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <TrendingUp className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Dados de SLA não disponíveis</p>
                  </div>
                )}
              </div>
            </CardContent>
          </React.Fragment>
        );
      default:
        return null;
    }
  };

  // Inicializar Swapy
  useEffect(() => {
    // Limpar Swapy anterior se existir
    if (swapyRef.current) {
      try {
        swapyRef.current.destroy();
        swapyRef.current = null;
      } catch (error) {
        console.error('Erro ao destruir Swapy anterior:', error);
      }
    }

    try {
      if (containerRef.current && cards && cards.length > 0) {
        // Verificar se há IDs duplicados
        const cardIds = cards.map(card => card.id);
        const uniqueIds = [...new Set(cardIds)];
        
        if (cardIds.length !== uniqueIds.length) {
          console.error('IDs duplicados detectados:', cardIds);
          return;
        }

        // Aguardar um tick para garantir que o DOM foi atualizado
        setTimeout(() => {
          if (containerRef.current) {
            swapyRef.current = createSwapy(containerRef.current, {
              animation: 'dynamic',
            });

            swapyRef.current.onSwap((event: any) => {
              // Salvar nova ordem no localStorage
              if (event.data && event.data.array) {
                const newOrder = event.data.array;
                
                // Atualizar ordem dos cards
                const updatedCards = [...allCards];
                newOrder.forEach((itemId: string, index: number) => {
                  const card = updatedCards.find(c => c.id === itemId);
                  if (card) {
                    card.order = index;
                  }
                });
                
                // Salvar no localStorage
                try {
                  localStorage.setItem('dashboard-layout', JSON.stringify(updatedCards));
                } catch (error) {
                  console.error('Erro ao salvar layout:', error);
                }
              }
            });
          }
        }, 100);
      }
    } catch (error) {
      console.error('Erro ao inicializar Swapy:', error);
    }

    return () => {
      if (swapyRef.current) {
        try {
          swapyRef.current.destroy();
          swapyRef.current = null;
        } catch (error) {
          console.error('Erro ao destruir Swapy:', error);
        }
      }
    };
  }, [cards, allCards]); // Dependência em cards para reinicializar quando visibilidade muda

  return (
    <div className="space-y-6">
      {/* Header com dica e controles */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* Dica sobre funcionalidades */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex-1">
          <div className="flex items-center gap-2 text-blue-700 text-sm">
            <Settings2 className="h-4 w-4" />
            <span className="font-medium">Dashboard Personalizável:</span>
            <span>Clique no ícone azul e arraste para mover • Clique no ícone verde para redimensionar</span>
          </div>
        </div>

        {/* Controles */}
        <div className="flex items-center gap-2">
          {/* Gerenciador de configurações avançadas */}
          <DashboardConfigManager />

          {/* Menu de visibilidade dos cards */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-10 px-3 gap-2 bg-white hover:bg-gray-50 border-gray-200"
              >
                <Eye className="h-4 w-4" />
                <span className="hidden sm:inline">Cards</span>
                <MoreHorizontal className="h-3 w-3 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Visibilidade dos Cards
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {allCards && allCards.map((card) => (
              <div key={card.id} className="flex items-center justify-between px-2 py-2 hover:bg-gray-50 rounded-sm mx-1">
                <div className="flex items-center gap-2 flex-1">
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    card.visible ? "bg-green-500" : "bg-gray-300"
                  )} />
                  <span className="text-sm font-medium text-gray-700 truncate">
                    {card.title}
                  </span>
                </div>
                <Switch
                  checked={card.visible}
                  onCheckedChange={() => {
                    toggleCardVisibility(card.id);
                    toast.success(
                      card.visible ? 'Card ocultado' : 'Card exibido',
                      {
                        description: `${card.title} ${card.visible ? 'foi removido' : 'foi adicionado'} ao dashboard`,
                        duration: 2000,
                      }
                    );
                  }}
                  className="ml-2"
                />
              </div>
            ))}
            <DropdownMenuSeparator />
            <div className="px-2 py-1">
              <div className="text-xs text-gray-500 text-center">
                {cards?.filter(c => c.visible).length || 0} de {allCards?.length || 0} cards visíveis
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </div>

      {/* Grid de Cards com Swapy - Layout Flexível */}
      <div 
        ref={containerRef}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 auto-rows-min"
      >
        {cards && cards.length > 0 ? cards.map((card, index) => (
          <div
            key={card.id}
            data-swapy-slot={card.id}
            className={cn(
              getCardClasses(card),
              "animate-in fade-in-0 slide-in-from-bottom-4 duration-300"
            )}
          >
            <div
              data-swapy-item={card.id}
              className="h-full"
              style={{ pointerEvents: 'none' }}
            >
              <ErrorBoundary key={`error-boundary-${card.id}`}>
                <Card className="relative h-full transition-all duration-200 hover:shadow-lg group" style={{ pointerEvents: 'auto' }}>
                  {/* Controles do card */}
                  <div 
                    className="absolute top-2 right-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 p-1 rounded-lg bg-white/80 backdrop-blur-sm shadow-sm"
                  >
                    {/* Menu de redimensionamento */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg cursor-pointer"
                          title="Redimensionar card"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          onDragStart={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <Settings2 className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem 
                          onClick={() => resizeCard(card.id, 'small')}
                        >
                          <Minimize2 className="h-4 w-4 mr-2" />
                          Pequeno (1x1)
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => resizeCard(card.id, 'medium')}
                        >
                          <Square className="h-4 w-4 mr-2" />
                          Médio (1x1)
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => resizeCard(card.id, 'wide')}
                        >
                          <RectangleHorizontal className="h-4 w-4 mr-2" />
                          Largo (2x1)
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => resizeCard(card.id, 'large')}
                        >
                          <RectangleVertical className="h-4 w-4 mr-2" />
                          Alto (1x2)
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => resizeCard(card.id, 'tall')}
                        >
                          <Maximize2 className="h-4 w-4 mr-2" />
                          Extra Alto (1x3)
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => resizeCard(card.id, 'extra-wide')}
                        >
                          <Monitor className="h-4 w-4 mr-2" />
                          Extra Largo (4x1)
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    {/* Indicador de drag */}
                    <div 
                      className="bg-blue-500 text-white p-1 rounded-full shadow-lg cursor-grab active:cursor-grabbing"
                      title="Clique e arraste para mover o card"
                      onMouseDown={(e) => {
                        // Habilitar drag temporariamente
                        const swapyItem = e.currentTarget.closest('[data-swapy-item]') as HTMLElement;
                        if (swapyItem) {
                          swapyItem.style.pointerEvents = 'auto';
                        }
                      }}
                      onMouseUp={(e) => {
                        // Desabilitar drag após soltar
                        setTimeout(() => {
                          const swapyItem = e.currentTarget.closest('[data-swapy-item]') as HTMLElement;
                          if (swapyItem) {
                            swapyItem.style.pointerEvents = 'none';
                          }
                        }, 100);
                      }}
                      onMouseLeave={(e) => {
                        // Desabilitar drag se o mouse sair do handle
                        setTimeout(() => {
                          const swapyItem = e.currentTarget.closest('[data-swapy-item]') as HTMLElement;
                          if (swapyItem) {
                            swapyItem.style.pointerEvents = 'none';
                          }
                        }, 100);
                      }}
                    >
                      <GripVertical className="h-3 w-3" />
                    </div>
                  </div>

                  {/* Conteúdo do card */}
                  <div>
                    {renderCardComponent(card.id)}
                  </div>
                </Card>
              </ErrorBoundary>
            </div>
          </div>
        )) : (
          <div className="col-span-full text-center py-16">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <EyeOff className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Nenhum card visível
              </h3>
              <p className="text-gray-500 mb-6">
                Todos os cards estão ocultos. Use o menu "Cards" para exibir os cards desejados.
              </p>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <Eye className="h-4 w-4" />
                    Mostrar Cards
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-64">
                  <DropdownMenuLabel className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Selecionar Cards
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {allCards && allCards.map((card) => (
                    <div key={card.id} className="flex items-center justify-between px-2 py-2 hover:bg-gray-50 rounded-sm mx-1">
                      <div className="flex items-center gap-2 flex-1">
                        <div className={cn(
                          "w-2 h-2 rounded-full",
                          card.visible ? "bg-green-500" : "bg-gray-300"
                        )} />
                        <span className="text-sm font-medium text-gray-700 truncate">
                          {card.title}
                        </span>
                      </div>
                      <Switch
                        checked={card.visible}
                        onCheckedChange={() => {
                          toggleCardVisibility(card.id);
                          toast.success('Card exibido', {
                            description: `${card.title} foi adicionado ao dashboard`,
                            duration: 2000,
                          });
                        }}
                        className="ml-2"
                      />
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default SwapyDashboard;
